'use client'

import { useState, useEffect } from 'react'
import { useLocale } from 'next-intl'
import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectCoverflow, Autoplay } from 'swiper/modules'
import { useSafeTranslations } from '@/hooks/useSafeTranslations'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/effect-coverflow'
import './Reviews.css'

// Only non-localizable fields kept here; text fields resolved via t() in JSX
const reviews = [
  { id: 1, name: 'Tesira',  rating: 5, platform: 'google',  reviewDate: '2025-08-15', helpful: 12, verified: true },
  { id: 2, name: 'Rob',     rating: 5, platform: 'airbnb',  reviewDate: '2025-07-22', helpful: 8,  verified: true },
  { id: 3, name: 'Jesse',   rating: 5, platform: 'google',  reviewDate: '2025-09-05', helpful: 15, verified: true },
  { id: 4, name: 'Pilar',   rating: 5, platform: 'booking', reviewDate: '2025-07-30', helpful: 23, verified: true },
  { id: 5, name: 'Vicente', rating: 5, platform: 'airbnb',  reviewDate: '2025-05-18', helpful: 6,  verified: true },
  { id: 6, name: 'Simone',  rating: 5, platform: 'google',  reviewDate: '2025-08-25', helpful: 19, verified: true },
]

type ReviewBase = typeof reviews[0]

// SVG Icons
const StarIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
)

const VerifiedIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
  </svg>
)

const LocationIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
  </svg>
)

const QuoteIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
  </svg>
)

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
  </svg>
)

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
  </svg>
)

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
  </svg>
)

const ThumbUpIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
  </svg>
)

// Platform icons
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
)

const AirbnbIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#FF5A5F" d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm5.5 17.5c-.3.5-.8.9-1.4 1.1-.6.2-1.3.1-1.9-.2-1.5-.8-2.7-2.1-3.5-3.7-.2-.4-.4-.8-.5-1.2-.1.4-.3.8-.5 1.2-.8 1.6-2 2.9-3.5 3.7-.6.3-1.3.4-1.9.2-.6-.2-1.1-.6-1.4-1.1-.3-.5-.4-1.1-.3-1.7.1-.6.4-1.1.8-1.5 1.2-1.2 2.7-2.1 4.4-2.5.4-.1.8-.2 1.2-.2v-.1c0-1.5-.5-2.9-1.4-4-.3-.4-.4-.9-.3-1.4.1-.5.4-.9.8-1.2.4-.3.9-.4 1.4-.3.5.1.9.4 1.2.8.9 1.1 1.4 2.5 1.4 4v.1c.4 0 .8.1 1.2.2 1.7.4 3.2 1.3 4.4 2.5.4.4.7.9.8 1.5.1.6 0 1.2-.3 1.7z"/>
  </svg>
)

const BookingIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <rect fill="#003580" width="24" height="24" rx="4"/>
    <text x="12" y="16" fill="white" fontSize="10" fontWeight="bold" textAnchor="middle" fontFamily="Arial">B</text>
  </svg>
)

const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case 'google': return <GoogleIcon />
    case 'airbnb': return <AirbnbIcon />
    case 'booking': return <BookingIcon />
    default: return null
  }
}

const getPlatformName = (platform: string) => {
  switch (platform) {
    case 'google': return 'Google'
    case 'airbnb': return 'Airbnb'
    case 'booking': return 'Booking.com'
    default: return platform
  }
}

const getPlatformColor = (platform: string) => {
  switch (platform) {
    case 'google': return '#4285F4'
    case 'airbnb': return '#FF5A5F'
    case 'booking': return '#003580'
    default: return '#d4af37'
  }
}

const formatReviewDate = (dateStr: string, locale: string) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
}

// Modal Component
function ReviewModal({
  review,
  onClose,
  locale,
}: {
  review: ReviewBase | null
  onClose: () => void
  locale: string
}) {
  const t = useSafeTranslations('homepage.reviews')

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (review) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [review, onClose])

  if (!review) return null

  const key = `review${review.id}` as const

  return (
    <div className="review-modal-overlay" onClick={onClose}>
      <div
        className="review-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ '--platform-color': getPlatformColor(review.platform) } as React.CSSProperties}
      >
        <button
          className="review-modal-close"
          onClick={onClose}
          aria-label={t('modal.close')}
        >
          <CloseIcon />
        </button>

        <div className="review-modal-platform-header">
          <div className="review-modal-platform-icon">
            {getPlatformIcon(review.platform)}
          </div>
          <div className="review-modal-platform-info">
            <span className="review-modal-platform-name">{getPlatformName(review.platform)}</span>
            <span className="review-modal-platform-verified">
              <ShieldIcon />
              {t('modal.verifiedReview')}
            </span>
          </div>
        </div>

        <div className="review-modal-rating">
          <div className="review-modal-stars">
            {[...Array(review.rating)].map((_, i) => (
              <StarIcon key={i} className="star-icon-modal" />
            ))}
          </div>
          <span className="review-modal-rating-text">{review.rating}.0 {t('modal.outOf5')}</span>
        </div>

        <div className="review-modal-content">
          <blockquote className="review-modal-text">
            &ldquo;{t(`items.${key}.fullText`)}&rdquo;
          </blockquote>
        </div>

        <div className="review-modal-reviewer">
          <div className="review-modal-avatar">
            {review.name.charAt(0)}
          </div>
          <div className="review-modal-reviewer-details">
            <span className="review-modal-reviewer-name">{review.name}</span>
            <span className="review-modal-reviewer-location">
              <LocationIcon />
              {t(`items.${key}.location`)}
            </span>
          </div>
          <div className="review-modal-verified-guest">
            <VerifiedIcon />
            <span>{t('modal.verifiedGuest')}</span>
          </div>
        </div>

        <div className="review-modal-meta">
          <div className="review-modal-meta-item">
            <CalendarIcon />
            <div className="review-modal-meta-content">
              <span className="review-modal-meta-label">{t('modal.dateOfStay')}</span>
              <span className="review-modal-meta-value">{t(`items.${key}.stayDate`)}</span>
            </div>
          </div>
          <div className="review-modal-meta-item">
            <ShieldIcon />
            <div className="review-modal-meta-content">
              <span className="review-modal-meta-label">{t('modal.experienceType')}</span>
              <span className="review-modal-meta-value">{t(`items.${key}.stayType`)}</span>
            </div>
          </div>
          <div className="review-modal-meta-item">
            <ThumbUpIcon />
            <div className="review-modal-meta-content">
              <span className="review-modal-meta-label">{t('modal.foundHelpful')}</span>
              <span className="review-modal-meta-value">{review.helpful} {t('modal.people')}</span>
            </div>
          </div>
        </div>

        <div className="review-modal-footer">
          <span className="review-modal-date">
            {t('modal.reviewedOn')} {formatReviewDate(review.reviewDate, locale)}
          </span>
          <span className="review-modal-import-badge">
            <ShieldIcon />
            {t('modal.importedFrom')} {getPlatformName(review.platform)}
          </span>
        </div>
      </div>
    </div>
  )
}

export default function Reviews() {
  const t = useSafeTranslations('homepage.reviews')
  const locale = useLocale()
  const [selectedReview, setSelectedReview] = useState<ReviewBase | null>(null)

  const handleReviewClick = (review: ReviewBase) => {
    setSelectedReview(review)
  }

  const handleCloseModal = () => {
    setSelectedReview(null)
  }

  return (
    <section className="reviews">
      <div className="reviews-bg-pattern" />
      <div className="reviews-glow-orb reviews-glow-orb--1" />
      <div className="reviews-glow-orb reviews-glow-orb--2" />

      <div className="reviews-container">
        {/* Header */}
        <div className="reviews-header">
          <span className="reviews-label">
            <ShieldIcon />
            <span>{t('label')}</span>
          </span>
          <h2 className="reviews-title">{t('title')}</h2>
          <p className="reviews-subtitle">{t('subtitle')}</p>
        </div>

        {/* Premium Swiper Carousel */}
        <div className="reviews-carousel">
          <Swiper
            modules={[EffectCoverflow, Autoplay]}
            effect="coverflow"
            coverflowEffect={{
              rotate: 0,
              stretch: 0,
              depth: 100,
              modifier: 2,
              slideShadows: false,
            }}
            centeredSlides={true}
            slidesPerView="auto"
            spaceBetween={30}
            loop={true}
            grabCursor={true}
            autoplay={{
              delay: 5000,
              disableOnInteraction: true,
              pauseOnMouseEnter: true,
            }}
            breakpoints={{
              640: {
                slidesPerView: 2,
                spaceBetween: 24,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 32,
              },
            }}
            className="reviews-swiper"
          >
            {reviews.map((review) => {
              const key = `review${review.id}` as const
              return (
                <SwiperSlide key={review.id} className="reviews-slide">
                  <article
                    className="review-card review-card--clickable"
                    onClick={() => handleReviewClick(review)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleReviewClick(review)
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="card-glow" />
                    <QuoteIcon className="quote-icon" />

                    <div className="review-header">
                      <span className="verified-badge">
                        <VerifiedIcon />
                        <span>{t('verifiedGuest')}</span>
                      </span>
                      <div className="review-stars">
                        {[...Array(review.rating)].map((_, i) => (
                          <StarIcon key={i} className="star-icon" />
                        ))}
                      </div>
                    </div>

                    <blockquote className="review-text">
                      &ldquo;{t(`items.${key}.shortText`)}&rdquo;
                    </blockquote>

                    <footer className="review-footer">
                      <div className="reviewer-avatar">
                        <span>{review.name.charAt(0)}</span>
                      </div>
                      <div className="reviewer-info">
                        <span className="reviewer-name">{review.name}</span>
                        <span className="reviewer-location">
                          <LocationIcon />
                          {t(`items.${key}.location`)}
                        </span>
                      </div>
                      <div className="platform-badge">
                        <span className="platform-icon">
                          {getPlatformIcon(review.platform)}
                        </span>
                        <span className="platform-name">{t('via')} {getPlatformName(review.platform)}</span>
                      </div>
                    </footer>
                  </article>
                </SwiperSlide>
              )
            })}
          </Swiper>
        </div>
      </div>

      {/* Modal */}
      <ReviewModal review={selectedReview} onClose={handleCloseModal} locale={locale} />
    </section>
  )
}
